import { useMemo, useCallback } from "react";
import { getCoreRowModel, useReactTable } from "@tanstack/react-table";
import debounceWithPromise from "views/logic/debounceWithPromise";
import { UTILITY_COLUMNS, ATTRIBUTE_STATUS, ACTIONS_COL_ID } from "../Constants";
const COLUMN_SIZING_PERSIST_DEBOUNCE_IN_MS = 500;
const columnVisibilityChanges = (prevVisibleColumns, currVisibleColumns) => {
  const addedColumns = /* @__PURE__ */ new Set();
  const removedColumns = /* @__PURE__ */ new Set();
  Object.keys(currVisibleColumns).forEach((key) => {
    if (currVisibleColumns[key] && !prevVisibleColumns[key]) {
      addedColumns.add(key);
    } else if (!currVisibleColumns[key] && prevVisibleColumns[key]) {
      removedColumns.add(key);
    }
  });
  return { addedColumns, removedColumns };
};
const updateColumnPreferences = (visibleAttributeColumns, removedColumns, columnPreferences = {}) => {
  const updatedPreferences = { ...columnPreferences };
  visibleAttributeColumns.forEach((col) => {
    updatedPreferences[col] = { status: ATTRIBUTE_STATUS.show };
  });
  removedColumns.forEach((col) => {
    updatedPreferences[col] = { status: ATTRIBUTE_STATUS.hide };
  });
  return updatedPreferences;
};
const useTable = ({
  columnOrder,
  columnDefinitions,
  columnWidths,
  defaultColumnOrder,
  displayBulkSelectCol,
  entities,
  headerMinWidths,
  isEntitySelectable = () => true,
  layoutPreferences,
  onChangeSelection,
  onLayoutPreferencesChange,
  onSortChange,
  selectedEntities,
  internalColumnWidthPreferences,
  setInternalColumnWidthPreferences,
  setInternalAttributeColumnOrder,
  setSelectedEntities,
  sort
}) => {
  const data = useMemo(() => [...entities], [entities]);
  const sorting = useMemo(() => sort ? [{ id: sort.attributeId, desc: sort.direction === "desc" }] : [], [sort]);
  const debouncedOnLayoutPreferencesChange = useMemo(
    () => debounceWithPromise(onLayoutPreferencesChange, COLUMN_SIZING_PERSIST_DEBOUNCE_IN_MS),
    [onLayoutPreferencesChange]
  );
  const onSortingChange = useCallback(
    (updater) => {
      const newSorting = updater instanceof Function ? updater(sorting) : updater;
      onSortChange({ attributeId: newSorting[0].id, direction: newSorting[0].desc ? "desc" : "asc" });
    },
    [onSortChange, sorting]
  );
  const columnVisibility = useMemo(
    () => Object.fromEntries(columnDefinitions.map(({ id }) => [id, columnOrder.includes(id)])),
    [columnDefinitions, columnOrder]
  );
  const onColumnVisibilityChange = useCallback(
    (updater) => {
      const newColumnVisibility = updater instanceof Function ? updater(columnVisibility) : updater;
      const visibleAttributeColumns = new Set(
        Object.keys(newColumnVisibility).filter((colId) => newColumnVisibility[colId] && !UTILITY_COLUMNS.has(colId))
      );
      const { addedColumns, removedColumns } = columnVisibilityChanges(columnVisibility, newColumnVisibility);
      const newLayoutPreferences = {
        attributes: updateColumnPreferences(visibleAttributeColumns, removedColumns, layoutPreferences.attributes)
      };
      if (layoutPreferences.order) {
        const newOrder = layoutPreferences.order.filter((colId) => !removedColumns.has(colId));
        [...addedColumns].forEach((colId) => {
          const defaultIdx = defaultColumnOrder.indexOf(colId);
          if (defaultIdx !== -1 && defaultIdx < newOrder.length) {
            newOrder.splice(defaultIdx, 0, colId);
          } else {
            newOrder.push(colId);
          }
        });
        newLayoutPreferences.order = newOrder;
      }
      return onLayoutPreferencesChange(newLayoutPreferences);
    },
    [
      columnVisibility,
      defaultColumnOrder,
      layoutPreferences.order,
      layoutPreferences.attributes,
      onLayoutPreferencesChange
    ]
  );
  const rowSelection = useMemo(() => Object.fromEntries(selectedEntities.map((id) => [id, true])), [selectedEntities]);
  const onRowSelectionChange = useCallback(
    (updater) => {
      const newRowSelection = updater instanceof Function ? updater(rowSelection) : updater;
      const newSelection = Object.entries(newRowSelection).filter(([_id, isSelected]) => isSelected).map(([id]) => id);
      setSelectedEntities(newSelection);
      if (onChangeSelection) {
        onChangeSelection(newSelection, entities);
      }
    },
    [entities, onChangeSelection, rowSelection, setSelectedEntities]
  );
  const onColumnOrderChange = useCallback(
    (updater) => {
      const newColumnOrder = (updater instanceof Function ? updater(columnOrder) : updater).filter(
        (colId) => !UTILITY_COLUMNS.has(colId)
      );
      setInternalAttributeColumnOrder(newColumnOrder);
      onLayoutPreferencesChange({ order: newColumnOrder });
    },
    [columnOrder, onLayoutPreferencesChange, setInternalAttributeColumnOrder]
  );
  const onColumnSizingChange = useCallback(
    (updater) => {
      const newAttributeWidthPreferences = updater instanceof Function ? updater(internalColumnWidthPreferences) : updater;
      const clampedAttributeWidths = Object.fromEntries(
        Object.entries(newAttributeWidthPreferences).map(([colId, width]) => [
          colId,
          Math.max(width, headerMinWidths[colId])
        ])
      );
      setInternalColumnWidthPreferences(clampedAttributeWidths);
      const newAttributePreferences = { ...(layoutPreferences == null ? void 0 : layoutPreferences.attributes) || {} };
      Object.entries(clampedAttributeWidths).forEach(([colId, width]) => {
        newAttributePreferences[colId] = {
          ...newAttributePreferences[colId],
          width
        };
      });
      return debouncedOnLayoutPreferencesChange({ attributes: newAttributePreferences });
    },
    [
      debouncedOnLayoutPreferencesChange,
      headerMinWidths,
      internalColumnWidthPreferences,
      layoutPreferences == null ? void 0 : layoutPreferences.attributes,
      setInternalColumnWidthPreferences
    ]
  );
  return useReactTable({
    columns: columnDefinitions,
    columnResizeMode: "onChange",
    data,
    enableRowSelection: (row) => displayBulkSelectCol && isEntitySelectable(row.original),
    enableSortingRemoval: false,
    getCoreRowModel: getCoreRowModel(),
    getRowId: (row) => row.id,
    manualSorting: true,
    onColumnOrderChange,
    onColumnVisibilityChange,
    onRowSelectionChange,
    onSortingChange,
    onColumnSizingChange,
    initialState: {
      columnPinning: {
        right: [ACTIONS_COL_ID]
      }
    },
    state: {
      columnOrder,
      columnVisibility,
      sorting,
      rowSelection,
      columnSizing: columnWidths
    }
  });
};
export default useTable;
